import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ErrorManagerService } from '@core/error-handling/error-manager';
import { RetryService } from '@core/retry';
import { ApplicationInsightsService, SeverityLevel } from '@core/telemetry';

export interface ErrorMetadata {
  retriesExhausted: boolean;
  isIdempotent: boolean;
}

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
) => {
  const errorManager = inject(ErrorManagerService);
  const retryService = inject(RetryService);
  const appInsights = inject(ApplicationInsightsService);

  const forceRetry = req.headers.has('X-Enable-Retry');
  const suppressError = req.headers.has('X-Suppress-Error');
  const isIdempotent = req.method === 'GET' || req.method === 'HEAD';
  const enableRetry = forceRetry || isIdempotent;

  const requestWithRetry = enableRetry
    ? retryService.retryWithBackoff(next(req), {
        maxRetries: 3,
        delay: 1000,
        retryCondition: (error) => {
          return (
            retryService.isRetryableError(error) &&
            error?.status !== 401 &&
            error?.status !== 403
          );
        },
      })
    : next(req);

  return requestWithRetry.pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const severity =
          error.status >= 500 ? SeverityLevel.Error : SeverityLevel.Warning;

        appInsights.trackException(
          new Error(`HTTP ${error.status}: ${error.message}`),
          {
            url: req.url,
            method: req.method,
            statusCode: error.status,
            statusText: error.statusText,
            traceId: error.error?.traceId,
          },
          severity
        );

        if (error.status === 401) {
          return throwError(() => error);
        }

        if (suppressError) {
          return throwError(() => error);
        }

        const metadata: ErrorMetadata = {
          retriesExhausted: enableRetry,
          isIdempotent,
        };

        errorManager.handleError(error, metadata);
      }

      return throwError(() => error);
    })
  );
};
